/**
 * Die **Skin** — hell oder dunkel, geschaltet im Benutzermenü.
 *
 * Der Rahmen legt die Mechanik fest (`FRAME.md`, „Benutzermenü"): Die Umschaltung
 * setzt `data-skin` am **Wurzelelement** und merkt sich die Wahl unter `erp-skin`,
 * und **das Produkt startet dunkel**. Dunkel ist damit nicht gespeicherter Zustand,
 * sondern der Rückfall: Was nicht `light` ist, ist dunkel — auch ein leerer, ein
 * fremder und ein fehlender Wert.
 *
 * **Angewandt wird die gemerkte Wahl beim Start und nicht erst beim Anmelden.**
 * `applyStoredSkin()` steht deshalb in `main.tsx`, vor dem ersten Zeichnen: Das
 * Anmeldebild ist ein eigener Schirm außerhalb der Hülle (#39), und wer hell
 * gewählt hat, will nicht zuerst ein dunkles Anmeldebild sehen.
 *
 * Der Ablageort kommt als Argument herein, statt dass `localStorage` hier
 * fest stünde — wie an der Gerätekennung in `api/client.ts` und aus demselben
 * Grund: Ein Test soll die Wahl setzen können, ohne den Speicher des Browsers zu
 * teilen.
 */

import { useEffect, useState } from "react";

/** Die beiden Skins. Englisch wie jeder Konfigurationswert (ADR-0009). */
export type Skin = "dark" | "light";

/**
 * Der Schlüssel, unter dem die Wahl liegt. Er kommt aus dem Rahmen und trägt
 * deshalb sein Präfix und nicht das dieser Anwendung.
 */
const SKIN_KEY = "erp-skin";

/** Die gemerkte Wahl — alles außer `light` ist dunkel. */
export function storedSkin(store: Storage = localStorage): Skin {
  return store.getItem(SKIN_KEY) === "light" ? "light" : "dark";
}

/** Die gemerkte Wahl an das Wurzelelement schreiben. */
export function applyStoredSkin(store: Storage = localStorage): void {
  document.documentElement.setAttribute("data-skin", storedSkin(store));
}

/**
 * Die Skin als Zustand des Benutzermenüs: welche gilt, und das Umschalten.
 *
 * Das Attribut wird im Effekt gesetzt und nicht beim Umschalten, damit beides
 * zusammenbleibt — auch beim ersten Zeichnen, wenn niemand geschaltet hat.
 */
export function useSkin(store: Storage = localStorage): {
  skin: Skin;
  choose: (next: Skin) => void;
} {
  const [skin, setSkin] = useState<Skin>(() => storedSkin(store));

  useEffect(() => {
    document.documentElement.setAttribute("data-skin", skin);
  }, [skin]);

  return {
    skin,
    choose: (next: Skin): void => {
      store.setItem(SKIN_KEY, next);
      setSkin(next);
    },
  };
}
